import { FormattedMessage } from 'react-intl';

import { Typography, TypographyProps } from '@mui/material';

import clsx from 'clsx';
import styled from 'styled-components';

import { Constants } from '@arrived/common';
import { AccountOfferingBalance, AssetType, Offering } from '@arrived/models';

import { StyledPropertyDetails } from '../../PropertyPage/PropertyDetails/PropertyDetails.styles';
import { PositionDetailSecondaryTypography } from './PositionDetailSecondaryTypography';

import { AwaitingValuationDescription } from '../../common/formattedMessages';
import {
  OfferingLeaseStatusMessage,
  PrivacyContextAwareMoneyValue,
  PrivacyContextAwareNumber,
  RENTAL_INCOME_COLOR,
} from '../common';
import { CellBodyTypography, EquityMetric, FirstDividendDate, RentalIncomePercentMetric } from '../common/cells';
import { StyledOfferingDialog } from '../desktop/table';

const StyledOfferingBalance = styled.section`
  --detailSectionGap: ${({ theme }) => theme.spacing(3)};
  --sectionGap: ${({ theme }) => theme.spacing(1)};

  .your-position-container {
    display: flex;
    flex-direction: row;
    column-gap: var(--detailSectionGap);
    row-gap: ${({ theme }) => theme.spacing(2)};
    margin-top: ${({ theme }) => theme.spacing(1)};
  }

  .your-position-section {
    display: flex;
    flex-direction: column;
    gap: var(--sectionGap);
    flex: 1 1 calc(50% - var(--detailSectionGap));
    border-top: 1px solid ${({ theme }) => theme.palette.neutrals.platinum};
    padding-top: ${({ theme }) => theme.spacing(2)};
    align-items: flex-start;
  }

  .your-position-section-group {
    display: flex;
    gap: var(--sectionGap);
    width: 100%;
  }

  .your-position-column {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    flex: 1 1 calc(50% - var(--sectionGap));
    position: relative;

    & > *:last-child {
      margin-top: auto;
    }
  }

  ${({ theme }) => theme.breakpoints.down('xl')} {
    ${StyledPropertyDetails} & {
      .your-position-container {
        flex-direction: column;
      }

      .position-details {
        flex: 1 1 auto;
      }
    }
  }

  ${StyledOfferingDialog} & {
    .rental-income-returns-header,
    .rental-status-text {
      white-space: nowrap;
    }
  }
`;

const PositionDetailHeaderTypography = ({ children, ...rest }: TypographyProps) => {
  return (
    <Typography color={({ palette }) => palette.darkened.steel} variant="body1" {...rest}>
      {children}
    </Typography>
  );
};

const PositionDetailTypography = ({ children, ...rest }: TypographyProps) => {
  return (
    <Typography variant="h5.semibold" {...rest}>
      {children}
    </Typography>
  );
};

export interface OfferingBalanceProps {
  appreciation: AccountOfferingBalance['appreciation'];
  className?: string;
  equity: AccountOfferingBalance['totalValue'];
  numShares: AccountOfferingBalance['totalSharesCount'];
  offering: Offering;
  rentalIncome: AccountOfferingBalance['rentalIncome'];
}

export const OfferingBalance = ({
  appreciation,
  className,
  equity,
  numShares,
  offering,
  rentalIncome,
}: OfferingBalanceProps) => {
  const showRentalStatus = offering.assetType === AssetType.LTR;

  return (
    <StyledOfferingBalance className={className}>
      <Typography variant="h6.semibold" color={({ palette }) => palette.darkened.steel}>
        <FormattedMessage id="your-position" defaultMessage="Your Position" />
      </Typography>
      <div className="your-position-container">
        <div className={clsx('your-position-section', 'rental-income')}>
          <div className="your-position-section-group">
            <div className="your-position-column">
              <PositionDetailHeaderTypography className="rental-income-returns-header">
                <FormattedMessage id="rental-income-returns" defaultMessage="Rental Income Returns" />
              </PositionDetailHeaderTypography>
              {rentalIncome.latestDividend != null ? (
                <PositionDetailTypography color={RENTAL_INCOME_COLOR}>
                  <PrivacyContextAwareMoneyValue length={5} value={rentalIncome.totalDividendsPaid} />
                </PositionDetailTypography>
              ) : (
                <PositionDetailTypography>
                  <FirstDividendDate offeringId={offering.id} />
                </PositionDetailTypography>
              )}
              <RentalIncomePercentMetric
                className="position-metric"
                lineHeight={1}
                value={rentalIncome.annualizedDividendPercent}
                latestDividend={rentalIncome.latestDividend}
              />
            </div>
            {showRentalStatus && (
              <div className="your-position-column">
                <PositionDetailHeaderTypography>
                  <FormattedMessage id="rental-status" defaultMessage="Rental Status" />
                </PositionDetailHeaderTypography>
                <PositionDetailSecondaryTypography className="rental-status-text">
                  <OfferingLeaseStatusMessage offeringId={offering.id} />
                </PositionDetailSecondaryTypography>
              </div>
            )}
          </div>
        </div>
        <div className={clsx('your-position-section', 'equity')}>
          <div className="your-position-section-group">
            <div className="your-position-column">
              <PositionDetailHeaderTypography>
                <FormattedMessage id="my-equity" defaultMessage="My Equity" />
              </PositionDetailHeaderTypography>
              <PositionDetailTypography>
                <PrivacyContextAwareMoneyValue length={5} value={equity} />
              </PositionDetailTypography>
              {appreciation != null && (
                <EquityMetric
                  appreciationInfo={Constants.sharePriceUpdatedOfferings.includes(offering.id) ? appreciation : null}
                  className="position-metric"
                  lineHeight={1}
                />
              )}
            </div>
            <div className="your-position-column">
              <PositionDetailSecondaryTypography>
                <FormattedMessage
                  id="share-count"
                  defaultMessage="{numShares} shares"
                  values={{
                    numShares: (
                      <PrivacyContextAwareNumber
                        minimumFractionDigits={0}
                        maximumFractionDigits={0}
                        length={3}
                        value={numShares}
                      />
                    ),
                  }}
                />
              </PositionDetailSecondaryTypography>
            </div>
          </div>
          {!Constants.sharePriceUpdatedOfferings.includes(offering.id) && (
            <CellBodyTypography>
              <AwaitingValuationDescription />
            </CellBodyTypography>
          )}
        </div>
      </div>
    </StyledOfferingBalance>
  );
};
